import { Location } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Capacitor } from '@capacitor/core';
import { AlertController, ModalController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, select, Store } from '@ngrx/store';
import { LOG_LEVEL, Purchases, PurchasesCallbackId, PurchasesStoreProduct } from '@revenuecat/purchases-capacitor';
import { loadStripe } from '@stripe/stripe-js';

import { compact, includes, isEmpty, isNull, isUndefined, map as lodashMap, values } from 'lodash-es';
import { delay, from, map, of, switchMap, tap, timer, withLatestFrom } from 'rxjs';
import { catchError, concatMap, filter, finalize, mergeMap } from 'rxjs/operators';
import { v4 as uuidV4 } from 'uuid';

import { virtualCurrencyLabel } from '../../constants/monetization.constant';
import { CoreConfig } from '../../core.module';
import {
  PhoneAndEmailPurchaseActionRoles,
  PrimarySubscriptionPlanAccessAliasEnum,
  StoreProductEnum,
  StripePaymentStatusEnum
} from '../../enums/monetization';
import { FarmSizeIdentifierEnum } from '../../enums/walking-farm';
import { getMainRouteByPropertySearchType } from '../../helpers/property-profile';
import { PhoneAndEmailPurchaseModalComponent } from '../../misc/pages/walking-farm/view/components/phone-and-email-purchase-modal/phone-and-email-purchase-modal';
import { AdminPortalApiService } from '../../misc/services/adminPortalApi.service';
import { LoadingService } from '../../misc/services/loading.service';
import { MonetizationService } from '../../misc/services/monetization.service';
import {
  MonetizationStripeProduct,
  RevenueCatWebAPICustomerInfo,
  RevenueCatWebAPIOfferings,
  StripeSubscriptionModel
} from '../../typings/monetization';
import {
  PhoneAndEmailPurchaseWalkingFarmProperty,
  WalkingFarmInAppPurchaseDetails,
  WalkingFarmPhoneAndEmailPurchasePreviewResponse
} from '../../typings/walking-farm';
import * as fromMonetizationActions from '../actions/monetization.actions';
import * as fromPersistedStateActions from '../actions/persisted-state.actions';
import * as fromUserActions from '../actions/user.actions';
import * as fromWalkingFarmActions from '../actions/walking-farm.actions';
import * as fromMonetizationSelectors from '../selectors/monetization.selectors';
import * as fromSettingSelectors from '../selectors/setting.selectors';
import * as fromUserSelectors from '../selectors/user.selectors';
import * as fromWalkingFarmSelectors from '../selectors/walking-farm.selectors';
@Injectable()
export class MonetizationEffects {
  private coreConfig = inject<CoreConfig>(CoreConfig);
  private actions$ = inject(Actions);
  private adminPortalApiService = inject(AdminPortalApiService);
  private alertController = inject(AlertController);
  private angularLocation = inject(Location);
  private httpClient = inject(HttpClient);
  private loadingService = inject(LoadingService);
  private modalController = inject(ModalController);
  private monetizationService = inject(MonetizationService);
  private router = inject(Router);
  private store = inject(Store);

  purchasesCallbackId: PurchasesCallbackId | null = null;

  loadMobileUserSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromUserActions.loadMobileUserSuccess, fromPersistedStateActions.migratePersistedOldStateUserDataSuccess),
      withLatestFrom(
        this.store.pipe(select(fromMonetizationSelectors.selectPrimarySubscriptionAccessAlias)),
        this.store.pipe(select(fromMonetizationSelectors.selectIsEnabled))
      ),
      filter((results) => results[2]),
      concatMap(
        ([
          {
            payload: { id }
          },
          accessAlias
        ]) => {
          const result: Action[] = [];
          if (accessAlias === PrimarySubscriptionPlanAccessAliasEnum.C) {
            result.push(fromWalkingFarmActions.loadSavedFarmsList());
          }
          if (Capacitor.isNativePlatform()) {
            result.push(fromMonetizationActions.initializeMobile({ payload: { id } }));
          } else {
            result.push(fromMonetizationActions.initializeWeb({ payload: { id } }));
          }
          return result;
        }
      )
    )
  );

  loadWebPurchasesOfferings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.initializeWeb),
      switchMap(({ payload: { id } }) => {
        const loadingInstanceID = uuidV4();
        this.loadingService.setLoading(loadingInstanceID, true);
        return this.httpClient
          .get<RevenueCatWebAPIOfferings>(`https://api.revenuecat.com/v1/subscribers/${id}/offerings`, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${this.coreConfig.apiKeys.revenueCat.web}`,
              'Content-Type': 'application/json',
              'X-Platform': 'stripe'
            }
          })
          .pipe(
            map((offerings) =>
              fromMonetizationActions.loadWebPurchasesOfferingsSuccess({
                data: { purchasesOfferings: offerings }
              })
            ),
            catchError(async (error) => {
              await this.monetizationService.showError({ error, subHeader: 'RevenueCat Load Offerings' });
              return fromMonetizationActions.loadWebPurchasesOfferingsFailure(error);
            }),
            finalize(() => {
              this.loadingService.setLoading(loadingInstanceID, false);
            })
          );
      })
    )
  );

  loadWebCustomerInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.initializeWeb),
      switchMap(({ payload: { id } }) => {
        const loadingInstanceID = uuidV4();
        this.loadingService.setLoading(loadingInstanceID, true);
        return this.httpClient
          .get<RevenueCatWebAPICustomerInfo>(`https://api.revenuecat.com/v1/subscribers/${id}`, {
            headers: {
              Accept: 'application/json',
              Authorization: `Bearer ${this.coreConfig.apiKeys.revenueCat.web}`,
              'Content-Type': 'application/json'
            }
          })
          .pipe(
            map((customerInfo) =>
              fromMonetizationActions.loadWebCustomerInfoSuccess({
                data: { customerInfo: customerInfo }
              })
            ),
            catchError(async (error) => {
              await this.monetizationService.showError({ error, subHeader: 'RevenueCat Load Customer' });
              return fromMonetizationActions.loadWebCustomerInfoFailure(error);
            }),
            finalize(() => {
              this.loadingService.setLoading(loadingInstanceID, false);
            })
          );
      })
    )
  );

  initializeMobile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.initializeMobile),
      switchMap(({ payload: { id } }) =>
        from(
          (async (id: string) => {
            const { isConfigured } = await Purchases.isConfigured();
            if (!isConfigured) {
              await Purchases.setLogLevel({ level: this.coreConfig.production ? LOG_LEVEL.ERROR : LOG_LEVEL.VERBOSE });
              await Purchases.configure({
                apiKey:
                  Capacitor.getPlatform() === 'ios'
                    ? this.coreConfig.apiKeys.revenueCat.ios
                    : this.coreConfig.apiKeys.revenueCat.android,
                appUserID: id
              });
            } else if ((await Purchases.getAppUserID()).appUserID !== id) {
              await Purchases.logIn({ appUserID: id });
              if (this.purchasesCallbackId) {
                await Purchases.removeCustomerInfoUpdateListener({ listenerToRemove: this.purchasesCallbackId });
                this.purchasesCallbackId = null;
              }
            }

            if (!this.purchasesCallbackId) {
              this.purchasesCallbackId = await Purchases.addCustomerInfoUpdateListener((customerInfo) => {
                this.store.dispatch(fromMonetizationActions.updateMobileCustomerInfo({ payload: { customerInfo } }));
              });
            }
            return fromMonetizationActions.initializeMobileSuccess();
          })(id)
        ).pipe(
          catchError(async (error) => {
            await this.monetizationService.showError({ error, subHeader: 'Native Initialization' });
            return fromMonetizationActions.initializeMobileFailure(error);
          })
        )
      )
    )
  );

  initializeMobileSuccess1$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.initializeMobileSuccess),
      switchMap(() =>
        from(Purchases.getOfferings()).pipe(
          map((purchasesOfferings) =>
            fromMonetizationActions.updateMobilePurchasesOfferings({ payload: { purchasesOfferings } })
          ),
          catchError(async (error) => {
            await this.monetizationService.showError({ error, subHeader: 'Native Purchase Offerings Load' });
            return fromMonetizationActions.updateMobilePurchasesOfferingsFailure(error);
          })
        )
      )
    )
  );

  initializeMobileSuccess2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.initializeMobileSuccess),
      switchMap(() =>
        from(Purchases.getCustomerInfo()).pipe(
          map(({ customerInfo }) => fromMonetizationActions.updateMobileCustomerInfo({ payload: { customerInfo } })),
          catchError(async (error) => {
            await this.monetizationService.showError({ error, subHeader: 'Native Customer Info Load' });
            return fromMonetizationActions.updateMobileCustomerInfoFailure(error);
          })
        )
      )
    )
  );

  initializeMobileSuccess3$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromMonetizationActions.initializeMobileSuccess),
        withLatestFrom(this.store.pipe(select(fromUserSelectors.selectDetails))),
        switchMap((results) =>
          from(Purchases.getCustomerInfo()).pipe(
            map(async ({ customerInfo }) => {
              const { appUserID } = await Purchases.getAppUserID();
              if (customerInfo.originalAppUserId !== appUserID) {
                try {
                  await Purchases.syncPurchases();
                } catch (error) {
                  await this.monetizationService.showError({ error: error as Error, subHeader: 'Sync Purchases' });
                }
              }
              await Promise.all([
                Purchases.setDisplayName({
                  displayName: compact([results[1].firstName, results[1].lastName]).join(' ')
                }),
                Purchases.setEmail({
                  email: results[1].email
                }),
                Purchases.setPhoneNumber({
                  phoneNumber: results[1].phone
                })
              ]);
            })
          )
        )
      ),
    {
      dispatch: false
    }
  );

  loadPrimarySubscriptionDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.loadPrimarySubscriptionDetails),
      withLatestFrom(
        this.store.pipe(select(fromMonetizationSelectors.selectPrimarySubscriptionPlatformProductIdentifier))
      ),
      map((results) => results[1]),
      filter((platformProductIdentifier) => !isNull(platformProductIdentifier)),
      switchMap((platformProductIdentifier) =>
        this.adminPortalApiService.sendRequest('GET', `/v2/subscriptions/details/${platformProductIdentifier}`).pipe(
          map(({ description }: { description: string; name: string }) =>
            fromMonetizationActions.loadPrimarySubscriptionDetailsSuccess({
              data: { description }
            })
          ),
          catchError(async (error) => {
            await this.monetizationService.showError({ error, subHeader: 'Load Subscription Details' });
            return fromMonetizationActions.loadPrimarySubscriptionDetailsFailure(error);
          })
        )
      )
    )
  );

  loadPrimarySubscriptionWebDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.loadPrimarySubscriptionDetails),
      filter(() => !Capacitor.isNativePlatform()),
      withLatestFrom(
        this.store.pipe(select(fromMonetizationSelectors.selectPrimarySubscriptionPlatformProductIdentifier))
      ),
      map((results) => results[1]),
      filter((platformProductIdentifier) => !isNull(platformProductIdentifier)),
      switchMap((platformProductIdentifier) =>
        this.adminPortalApiService.sendRequest('GET', `/v2/stripe/product/${platformProductIdentifier}`).pipe(
          map((data: MonetizationStripeProduct) =>
            fromMonetizationActions.loadPrimarySubscriptionWebDetailsSuccess({
              data
            })
          ),
          catchError(async (error) => {
            await this.monetizationService.showError({ error, subHeader: 'Stripe Load Subscription' });
            return fromMonetizationActions.loadPrimarySubscriptionWebDetailsFailure(error);
          })
        )
      )
    )
  );

  loadStoreWebDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.loadStoreWebDetails),
      filter(() => !Capacitor.isNativePlatform()),
      withLatestFrom(this.store.pipe(select(fromMonetizationSelectors.selectStorePlatformProductIdentifiers))),
      filter((results) => results[1].length > 0),
      map((results) => results[1]),
      switchMap((storePlatformProductIdentifiers) =>
        this.adminPortalApiService
          .sendRequest('POST', `/v2/stripe/products`, {
            body: {
              ids: storePlatformProductIdentifiers
            }
          })
          .pipe(
            map((payload: MonetizationStripeProduct[]) =>
              fromMonetizationActions.loadStoreWebDetailsSuccess({
                payload
              })
            ),
            catchError(async (error: Error) => {
              await this.monetizationService.showError({ error, subHeader: 'Stripe Load Store Details' });
              return fromMonetizationActions.loadStoreWebDetailsFailure({ payload: { error } });
            })
          )
      )
    )
  );

  updateSelectedStoreWebProductDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.updateSelectedStoreWebProductDetails),
      filter(({ payload: { platformProductIdentifier } }) => !isNull(platformProductIdentifier)),
      switchMap(({ payload: { platformProductIdentifier, type } }) =>
        this.adminPortalApiService
          .sendRequest(
            'GET',
            `/v2/${type === StoreProductEnum.Subscription ? 'subscriptions' : 'in-app-purchases'}/details/${platformProductIdentifier}`
          )
          .pipe(
            map(({ description }: { description: string; name: string }) =>
              fromMonetizationActions.updateSelectedStoreWebProductDetailsSuccess({
                payload: { description }
              })
            ),
            catchError(async (error: Error) => {
              await this.monetizationService.showError({ error, subHeader: 'Load Product Details' });
              return fromMonetizationActions.updateSelectedStoreWebProductDetailsFailure({ payload: { error } });
            })
          )
      )
    )
  );

  logUserPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.logUserPurchase),
      withLatestFrom(
        this.store.pipe(select(fromMonetizationSelectors.selectStoreSelectedProductInternalIdentifier)),
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromSettingSelectors.selectParseType))
      ),
      filter((results) => !isNull(results[1])),
      map((results) => ({
        id: results[2],
        internalIdentifier: results[1],
        parseType: results[3],
        resumePurchaseFlow: results[0].payload.resumePurchaseFlow,
        searchType: results[0].payload.searchType
      })),
      switchMap(({ id, internalIdentifier, parseType, resumePurchaseFlow, searchType }) =>
        this.adminPortalApiService
          .sendRequest('POST', `/v2/mobile-users/${id}/purchase/${internalIdentifier}`, {
            params: this.adminPortalApiService.getQueryParams({
              parseType
            })
          })
          .pipe(
            switchMap(() => [
              fromMonetizationActions.logUserPurchaseSuccess({
                payload: {
                  resumePurchaseFlow,
                  searchType
                }
              }),
              fromUserActions.reloadMobileUser()
            ]),
            catchError(async (error: Error) => {
              await this.monetizationService.showError({ error, subHeader: 'Log User Purchase' });
              return fromMonetizationActions.logUserPurchaseFailure({ payload: { error } });
            })
          )
      )
    )
  );

  paywallWebPurchaseSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.paywallPurchase),
      withLatestFrom(
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromUserSelectors.selectEmail))
      ),
      filter(
        ([
          {
            data: { webPriceId }
          }
        ]) => !Capacitor.isNativePlatform() && !isNull(webPriceId)
      ),
      switchMap(([{ data, searchType }, clientReferenceId, customerEmail]) =>
        this.adminPortalApiService
          .sendRequest('POST', `/v2/stripe/checkout/sessions`, {
            body: {
              clientReferenceId,
              container: this.coreConfig.container,
              customerEmail,
              mode: data.purchaseType,
              priceId: data.webPriceId,
              uiCancelUrl: `${location.protocol}//${window.location.host}/#/payment/redirect/${StripePaymentStatusEnum.Failure}`,
              uiSuccessUrl: `${location.protocol}//${window.location.host}/#/payment/redirect/${StripePaymentStatusEnum.Success}`
            }
          })
          .pipe(
            map((response: { id: string }) =>
              fromMonetizationActions.paywallWebPurchase({
                payload: {
                  redirectBackOnSuccess: data.redirectBackOnSuccess,
                  searchType,
                  sessionId: response.id
                }
              })
            ),
            catchError(async (error) => {
              await this.monetizationService.showError({ error, subHeader: 'Stripe Purchase' });
              return fromMonetizationActions.paywallWebPurchaseFailure(error);
            })
          )
      )
    )
  );

  paywallWebPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.paywallWebPurchase),
      map(({ payload: { redirectBackOnSuccess, searchType, sessionId } }) =>
        fromMonetizationActions.persistAndRedirectToPurchase({
          payload: {
            redirectBackOnSuccess,
            redirectBackUrl: this.router.url,
            searchType,
            sessionId
          }
        })
      )
    )
  );

  persistAndPurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.persistAndRedirectToPurchase),
      switchMap(({ payload: { sessionId } }) => {
        const loadingInstanceID = uuidV4();
        this.loadingService.setLoading(loadingInstanceID, true);
        return from(loadStripe(this.coreConfig.apiKeys.stripe.publicKey)).pipe(
          filter((stripe) => !isNull(stripe)),
          delay(2000),
          switchMap((stripe) =>
            from(stripe.redirectToCheckout({ sessionId })).pipe(
              map(() => fromMonetizationActions.paywallWebPurchaseSuccess())
            )
          ),
          finalize(() => {
            this.loadingService.setLoading(loadingInstanceID, false);
          })
        );
      }),
      catchError(async (error) => {
        await this.monetizationService.showError({ error, subHeader: 'Stripe Purchase' });
        return fromMonetizationActions.paywallWebPurchaseFailure(error);
      })
    )
  );

  paywallMobilePurchase$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.paywallPurchase),
      filter(() => Capacitor.isNativePlatform()),
      filter(({ data: { product } }) => !isNull(product)),
      switchMap(({ data: { product, redirectBackOnSuccess }, searchType }) => {
        const loadingInstanceID = uuidV4();
        this.loadingService.setLoading(loadingInstanceID, true);
        return from(Purchases.purchaseStoreProduct({ product: product as PurchasesStoreProduct }))
          .pipe(
            map(({ customerInfo }) =>
              fromMonetizationActions.paywallMobilePurchaseSuccess({
                payload: { customerInfo, redirectBackOnSuccess, searchType }
              })
            ),
            finalize(() => {
              this.loadingService.setLoading(loadingInstanceID, false);
            })
          )
          .pipe(
            catchError(async (error: Error) => {
              await this.monetizationService.showError({ error, header: 'Payment Failed' });
              return fromMonetizationActions.paywallMobilePurchaseFailure({ payload: { error } });
            })
          );
      })
    )
  );

  paywallMobilePurchaseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.paywallMobilePurchaseSuccess),
      tap(() => this.modalController.dismiss(null, 'cancel')),
      map(({ payload: { redirectBackOnSuccess, searchType } }) =>
        fromMonetizationActions.logUserPurchase({
          payload: { resumePurchaseFlow: redirectBackOnSuccess, searchType }
        })
      )
    )
  );

  logUserPurchaseSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.logUserPurchaseSuccess),
      filter(({ payload: { resumePurchaseFlow } }) => resumePurchaseFlow),
      tap(() => this.angularLocation.back()),
      map(({ payload: { searchType } }) =>
        fromMonetizationActions.resumePurchasePhoneAndEmailPreview({ payload: { searchType } })
      )
    )
  );

  resumePurchasePhoneAndEmailPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.resumePurchasePhoneAndEmailPreview),
      withLatestFrom(this.store.pipe(select(fromMonetizationSelectors.selectPhoneAndEmailPurchasePreviewData))),
      filter((data) => !isNull(data[1].farmId)),
      map(([{ payload }, previewData]) =>
        fromMonetizationActions.purchasePhoneAndEmailPreview({
          payload: {
            farmId: previewData.farmId ?? '',
            propertyIds: previewData.propertyIds,
            searchType: payload.searchType
          }
        })
      )
    )
  );

  loadSubscriptions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.loadSubscriptions, fromMonetizationActions.cancelSubscriptionSuccess),
      withLatestFrom(this.store.pipe(select(fromUserSelectors.selectEmail))),
      map((results) => results[1]),
      switchMap((email) =>
        this.adminPortalApiService.sendRequest('GET', `/v2/stripe/subscriptions/${email}`).pipe(
          map((payload: StripeSubscriptionModel[]) => fromMonetizationActions.loadSubscriptionsSuccess({ payload })),
          catchError(async (error: Error) => {
            await this.monetizationService.showError({ error, subHeader: 'Stripe Load Subscription List' });
            return fromMonetizationActions.loadSubscriptionsFailure({ payload: { error } });
          })
        )
      )
    )
  );

  cancelSubscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.cancelSubscription),
      switchMap(({ payload: { id } }) =>
        this.adminPortalApiService.sendRequest('DELETE', `/v2/stripe/subscriptions/${id}`).pipe(
          map(() => fromMonetizationActions.cancelSubscriptionSuccess()),
          catchError(async (error: Error) => {
            await this.monetizationService.showError({ error, subHeader: 'Stripe Cancel Subscription' });
            return fromMonetizationActions.cancelSubscriptionFailure({ payload: { error } });
          })
        )
      )
    )
  );

  purchasePhoneAndEmailPreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.purchasePhoneAndEmailPreview),
      withLatestFrom(
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromSettingSelectors.selectParseType))
      ),
      tap(() => this.modalController.dismiss(null, 'cancel')),
      switchMap(([{ payload }, userId, parseType]) =>
        this.adminPortalApiService
          .sendRequest(
            'POST',
            `/v2/mobile-users/${userId}/virtual-currency-purchase/PHONES_AND_EMAILS/preview`,
            {
              body: {
                farm: {
                  id: payload.farmId,
                  propertyIds: payload.propertyIds
                }
              },
              params: this.adminPortalApiService.getQueryParams({
                parseType
              })
            },
            {
              shouldUseErrorMessageFromDescription: true
            }
          )
          .pipe(
            map((preview: WalkingFarmPhoneAndEmailPurchasePreviewResponse) =>
              fromMonetizationActions.purchasePhoneAndEmailPreviewSuccess({
                payload: {
                  farmId: payload.farmId,
                  preview,
                  propertyIds: payload.propertyIds,
                  searchType: payload.searchType
                }
              })
            ),
            catchError(async (error: Error) => {
              await this.monetizationService.showError({
                error,
                header: 'Purchase Preview Error',
                subHeader: 'Phones And Emails'
              });
              return fromMonetizationActions.purchasePhoneAndEmailPreviewFailure({ payload: { error } });
            })
          )
      )
    )
  );

  purchasePhoneAndEmailPreviewSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.purchasePhoneAndEmailPreviewSuccess),
      switchMap(async ({ payload }) => {
        const alert = await this.alertController.create({
          buttons: payload.preview.canPurchase
            ? [
                {
                  role: PhoneAndEmailPurchaseActionRoles.Cancel,
                  text: 'Cancel'
                },
                {
                  role: PhoneAndEmailPurchaseActionRoles.Confirm,
                  text: 'Continue'
                }
              ]
            : [
                {
                  role: PhoneAndEmailPurchaseActionRoles.Change,
                  text: 'Change Selection'
                },
                {
                  role: PhoneAndEmailPurchaseActionRoles.Store,
                  text: 'Purchase More'
                }
              ],
          header: 'Phone/Email Search',
          message:
            `Obtaining the phone numbers and email addresses for the properties you’ve selected could consume up to ${payload.preview.cost} ${virtualCurrencyLabel}. ` +
            `You currently have ${payload.preview.available} ${virtualCurrencyLabel}. ` +
            `${
              payload.preview.canPurchase
                ? 'Do you wish to continue?'
                : `You can purchase more ${virtualCurrencyLabel} or select fewer properties for the search.`
            }`
        });

        await alert.present();
        const { role } = await alert.onDidDismiss();
        return {
          farmId: payload.farmId,
          propertyIds: payload.propertyIds,
          role,
          searchType: payload.searchType
        };
      }),
      filter(({ role }) =>
        includes(
          [
            PhoneAndEmailPurchaseActionRoles.Confirm,
            PhoneAndEmailPurchaseActionRoles.Store,
            PhoneAndEmailPurchaseActionRoles.Change
          ],
          role
        )
      ),
      map(({ farmId, propertyIds, role, searchType }) => {
        let action: Action;
        switch (role) {
          case PhoneAndEmailPurchaseActionRoles.Confirm:
            action = fromMonetizationActions.purchasePhoneAndEmailConfirm({
              payload: {
                farmId,
                propertyIds
              }
            });
            break;
          case PhoneAndEmailPurchaseActionRoles.Store:
            action = fromMonetizationActions.purchasePhoneAndEmailPurchaseMore({ payload: { searchType } });
            break;
          default:
            action = fromMonetizationActions.openPurchasePhoneAndEmailModal({
              payload: {
                farmId,
                searchType
              }
            });
            break;
        }
        return action;
      })
    )
  );

  purchasePhoneAndEmailPurchaseMore$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromMonetizationActions.purchasePhoneAndEmailPurchaseMore),
        switchMap(async ({ payload: { searchType } }) => {
          await this.router.navigate([getMainRouteByPropertySearchType(searchType), 'store'], {
            queryParams: {
              redirectBackOnSuccess: true
            }
          });
        })
      ),
    {
      dispatch: false
    }
  );

  purchasePhoneAndEmailConfirm$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.purchasePhoneAndEmailConfirm),
      withLatestFrom(
        this.store.pipe(select(fromUserSelectors.selectId)),
        this.store.pipe(select(fromSettingSelectors.selectParseType))
      ),
      switchMap(
        ([
          {
            payload: { farmId, propertyIds }
          },
          userId,
          parseType
        ]) =>
          this.adminPortalApiService
            .sendRequest(
              'POST',
              `/v2/mobile-users/${userId}/virtual-currency-purchase/PHONES_AND_EMAILS`,
              {
                body: {
                  farm: {
                    id: farmId,
                    propertyIds: propertyIds
                  }
                },
                params: this.adminPortalApiService.getQueryParams({
                  parseType
                })
              },
              {
                shouldUseErrorMessageFromDescription: true
              }
            )
            .pipe(
              concatMap(async (data: { success: boolean }) => {
                const alert = await this.alertController.create({
                  buttons: [
                    {
                      role: 'cancel',
                      text: 'Cancel'
                    },
                    {
                      role: 'cancel',
                      text: 'Ok'
                    }
                  ],
                  header: 'Notification',
                  message: data.success
                    ? 'Thank you for purchasing. It might take some time to fetch and update the farm with the new information. You will receive a email when the process is complete.'
                    : 'There was an issue handling your request. Please try again later.'
                });
                await alert.present();
              }),
              map(() => fromMonetizationActions.purchasePhoneAndEmailSuccess()),
              catchError(async (error: Error) => {
                await this.monetizationService.showError({
                  error,
                  header: 'Purchase Error',
                  subHeader: 'Phones And Emails'
                });
                return fromMonetizationActions.purchasePhoneAndEmailFailure({ payload: { error } });
              })
            )
      )
    )
  );

  openPurchasePhoneAndEmailModal$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromMonetizationActions.openPurchasePhoneAndEmailModal),
        mergeMap(({ payload: { farmId, searchType } }) =>
          of(farmId).pipe(
            withLatestFrom(
              this.store.pipe(select(fromWalkingFarmSelectors.selectWalkingFarmProperties(farmId))),
              this.store.pipe(
                select(fromWalkingFarmSelectors.selectWalkingFarmInAppPurchases(farmId)),
                map((data) => {
                  let purchaseDetails: WalkingFarmInAppPurchaseDetails | undefined = undefined;
                  if (data) {
                    for (const key in data) {
                      if (includes(values<string>(FarmSizeIdentifierEnum), key)) {
                        purchaseDetails = data[key as FarmSizeIdentifierEnum];
                      }
                    }
                  }
                  return purchaseDetails;
                })
              ),
              this.store.pipe(
                select(fromWalkingFarmSelectors.selectWalkingFarmPhoneAndEmailSelectedPropertyIds(farmId))
              )
            ),
            map((result) => ({
              farmId,
              properties: result[1],
              propertyIds: result[3],
              purchaseDetails: result[2],
              searchType
            }))
          )
        ),
        concatMap(async ({ farmId, properties, propertyIds, purchaseDetails, searchType }) => {
          const modal = await this.modalController.create({
            component: PhoneAndEmailPurchaseModalComponent,
            componentProps: {
              farmId,
              properties: lodashMap(properties, (property): PhoneAndEmailPurchaseWalkingFarmProperty => ({
                ...property,
                phoneAndEmailIsPurchased: includes(
                  purchaseDetails?.purchasedPropertyIds,
                  `${property.A000_PropertyNumber}`
                ),
                phoneAndEmailIsSelected:
                  !isUndefined(propertyIds) && !isEmpty(propertyIds)
                    ? propertyIds.includes(`${property.A000_PropertyNumber}`)
                    : true
              })),
              searchType
            }
          });

          await modal.present();
        })
      ),
    {
      dispatch: false
    }
  );

  purchasePhoneAndEmailSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.purchasePhoneAndEmailSuccess),
      concatMap(() => {
        const loadingInstanceID = uuidV4();
        this.loadingService.setLoading(loadingInstanceID, true);
        return timer(5000).pipe(
          map(() => fromUserActions.reloadMobileUser()),
          finalize(() => {
            this.loadingService.setLoading(loadingInstanceID, false);
          })
        );
      })
    )
  );

  restorePurchases$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromMonetizationActions.restorePurchases),
      switchMap(() => {
        const loadingInstanceID = uuidV4();
        this.loadingService.setLoading(loadingInstanceID, true);
        return from(Purchases.restorePurchases()).pipe(
          map(({ customerInfo }) => fromMonetizationActions.updateMobileCustomerInfo({ payload: { customerInfo } })),
          catchError(async (error) => {
            await this.monetizationService.showError({ error, subHeader: 'Restore Purchases' });
            return fromMonetizationActions.restorePurchasesFailure(error);
          }),
          finalize(() => {
            this.loadingService.setLoading(loadingInstanceID, false);
          })
        );
      })
    )
  );
}
